import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WarningOctagon, PauseCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtIST } from "@/lib/utils";

// Red banner on the SOC dashboard when email ingest is paused, the last poll failed, or the
// poller has gone quiet (no completed poll in 3x the interval). Leads can resume in place.
export function SocPollerBanner({ isLead }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/soc/mail-poll").then(r => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (!st || !st.creds_ready || !st.mailbox) return null;
  const err = st.last_error;
  const staleMs = Math.max(st.interval_seconds || 120, 60) * 3 * 1000;
  const stale = st.poll_enabled && st.last_poll_at && (Date.now() - new Date(st.last_poll_at).getTime() > staleMs);
  if (st.poll_enabled && !err && !stale) return null;

  const resume = async () => {
    setBusy(true);
    try { const { data } = await api.put("/soc/mail-poll", { enabled: true }); setSt(data); toast.success("SOC polling resumed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resume polling"); }
    finally { setBusy(false); }
  };

  let title, detail;
  if (!st.poll_enabled) {
    title = "SOC email polling is paused";
    detail = `New emails to ${st.mailbox} are NOT becoming cases automatically.`;
  } else if (err) {
    title = err.stage ? "SOC email poll: move to processed folder failed" : "SOC email poll failed";
    detail = `${err.status ? `HTTP ${err.status} ` : ""}${err.code ? `${err.code} — ` : ""}${err.message || ""}${err.at ? ` (${fmtIST(err.at)})` : ""}`;
  } else {
    title = "SOC email poller has gone quiet";
    detail = `No completed poll since ${fmtIST(st.last_poll_at)} (expected every ${Math.round(st.interval_seconds / 60)} min).`;
  }

  return (
    <div data-testid="soc-poller-banner" role="alert"
      className="flex items-start sm:items-center justify-between gap-3 flex-wrap rounded-sm border border-rag-red-fg/50 bg-rag-red-bg/40 px-4 py-3">
      <div className="flex items-start gap-3 min-w-0">
        {st.poll_enabled ? <WarningOctagon size={20} weight="fill" className="text-rag-red-fg shrink-0 mt-0.5" /> : <PauseCircle size={20} weight="fill" className="text-rag-red-fg shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <div data-testid="soc-poller-banner-title" className="text-sm font-semibold text-rag-red-fg">{title}</div>
          <div className="text-xs text-foreground/80 break-words">{detail}</div>
          {err?.hint && st.poll_enabled && <div className="text-[11px] text-muted-foreground mt-0.5 break-words">{err.hint}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:shrink-0">
        {isLead && !st.poll_enabled && (
          <Button data-testid="soc-poller-banner-resume" onClick={resume} disabled={busy} size="sm" className="rounded-sm font-mono text-xs uppercase tracking-widest">
            {busy ? "Resuming…" : "Resume polling"}
          </Button>
        )}
        {isLead && (
          <Button asChild variant="outline" size="sm" className="rounded-sm font-mono text-xs uppercase tracking-widest border-rag-red-fg/40">
            <Link to="/soc/settings" data-testid="soc-poller-banner-settings"><ArrowSquareOut size={13} className="mr-1" /> Email ingest settings</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
